import React from "react";
import { compose } from "redux";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import {
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
} from "@material-ui/core";

// HOCs
import withRecords, { WithRecordsProps } from "../../hoc/withRecords";
import withConclusion, { WithConclusionProps } from "../../hoc/withConclusion";
import withApproveDialog, {
  WithApproveDialogProps,
} from "../../hoc/withApproveDialog";
import withRecordApprove, {
  WithRecordApproveProps,
} from "../../hoc/withRecordApprove";

// Components
import Button from "../../../../components/Button";

// Styles
import useStyles from "./styles";
import withInterpretation, {
  WithInterpretationProps,
} from "../../hoc/withInterpretation";

import { AppRoute } from "../../../routing/constants";

type ApproveDialogProps = RouteComponentProps &
  WithApproveDialogProps &
  WithRecordsProps &
  WithRecordApproveProps &
  WithConclusionProps &
  WithInterpretationProps;

/**
 * @param props
 */
function ApproveDialog(props: ApproveDialogProps) {
  const {
    approveDialogActive,
    setApproveDialogActive,
    receiveNext,
    setReceiveNext,
    currentApproving,
    approveRecord,
    currentRecord,
    conclusion,
    interpretation,
    history,
    recordApproved,
  } = props;
  const styles = useStyles();
  const [t] = useTranslation("common");

  const [dialogConclusion, setDialogConclusion] = React.useState<string | null>(
    null
  );
  const [dialogInterpretation, setDialogInterpretation] = React.useState<
    string | null
  >(null);

  const handleDialogClose = () => setApproveDialogActive(false);
  const handleReceiveNextChange = () => setReceiveNext(!receiveNext);

  React.useEffect(() => {
    if (approveDialogActive) {
      setDialogConclusion(conclusion);
      setDialogInterpretation(interpretation);
    }
  }, [approveDialogActive, conclusion, interpretation]);

  React.useLayoutEffect(() => {
    if (
      approveDialogActive &&
      (interpretation === undefined ||
        interpretation === null ||
        interpretation.trim() === "")
    )
      setTimeout(() => {
        alert("Interpretation is blank.");
      }, 10);
    // eslint-disable-next-line
  }, [interpretation, approveDialogActive]);

  function renderControl() {
    return (
      <Checkbox
        color="primary"
        value="checkedA"
        checked={receiveNext}
        onChange={handleReceiveNextChange}
      />
    );
  }

  if (recordApproved && !receiveNext) {
    console.log("Redirecting to home...");
    history.replace(AppRoute.Home);
  }

  const displayConclusion =
    dialogConclusion !== null ? dialogConclusion : conclusion;
  const displayInterpretation =
    dialogInterpretation !== null ? dialogInterpretation : interpretation;

  return (
    <Dialog
      open={approveDialogActive}
      onClose={handleDialogClose}
      PaperProps={{ className: styles.wrapper }}>
      <DialogTitle>{t("approve_modal.title")}</DialogTitle>
      <DialogContent>
        <Box className={styles.conclusion_container}>
          <Typography className={styles.conclusion_title}>
            {t("records.conclusion")}:
          </Typography>
          <Typography component="p">{displayConclusion}</Typography>
        </Box>
        <Box className={styles.conclusion_container}>
          <Typography className={styles.conclusion_title}>
            {t("interpretation.title")}:
          </Typography>
          <Typography component="p">{displayInterpretation}</Typography>
        </Box>
        <FormControlLabel
          control={renderControl()}
          className={styles.checkbox_container}
          label={t("approve_modal.automatically_next")}
        />
      </DialogContent>
      <DialogActions className={styles.actions}>
        <Button
          size="medium"
          color="secondary"
          label={t("cancel")}
          onClick={handleDialogClose}
          className={styles.footer_button}
        />
        <Button
          size="medium"
          color="primary"
          variant="contained"
          label={t("approve")}
          loading={currentApproving}
          className={styles.footer_button}
          onClick={() => approveRecord(currentRecord!.teleRecordId)}
        />
      </DialogActions>
    </Dialog>
  );
}

export default compose(
  withConclusion,
  withInterpretation,
  withApproveDialog,
  withRecordApprove,
  withRecords,
  withRouter
)(ApproveDialog);
